import type { GenData } from '../schemas/defs.js';
import { BALANCE } from './balance.js';

/**
 * Generator data for daily runs (TRD-02 §8b, TRD-13).
 *
 * The generator ALGORITHM lives in `packages/sim/src/gen/`; everything it reads lives
 * here. A magic number inside the generator is a bug — TRD-13 T-11 tests for exactly that
 * by perturbing `baseThreat` and asserting the output changes.
 */
export const GEN_DATA: GenData = {
  /**
   * v1 ships exactly one biome (R-02-14, TRD-12 A-15). The array stays plural so adding
   * environments later is a content change rather than an engine change, and `rollBiome`
   * still consumes an RNG draw so the stream position does not shift when more arrive.
   */
  biomes: [
    {
      id: 'ashen-flat',
      name: 'Ashen Flat',
      tileset: 'atlas_tiles',
      // Nothing above #40 brightness — this is the entire art direction (TRD-12 A-03b).
      palette: {
        void: '#0e0e10',
        outline: '#08080a',
        blocked: '#141416',
        groundDark: '#1a1a1d',
        groundMid: '#232327',
        groundLight: '#2c2c31',
        pathDark: '#34343a',
        pathLight: '#3f3f46',
      },
      terrainDensity: 0.16,
      // 0 for v1: decorative overlay tiles are deferred (TRD-12 §10). The code path exists
      // regardless, because retrofitting it later means touching the generator and
      // invalidating replays (TRD-13 R-13-18).
      decorDensity: 0,
      corePlacement: 'edge-biased',
      weight: 1,
    },
  ],

  /**
   * Threat cost per enemy — the currency the wave generator spends (TRD-13 R-13-23).
   *
   * These are the primary difficulty dial for generated waves. Roughly proportional to
   * `hp × speed`, adjusted upward for archetypes that demand a specific answer: a Ward
   * costs more than its stats suggest because a board without burst simply cannot kill it.
   */
  threatCosts: {
    runner: 1,
    sprinter: 0.7,
    brute: 5,
    ward: 4.5,
    splitter: 3.5,
    splitterling: 0.6,
    mender: 4,
    drifter: 2.5,
    shrike: 2.2,
    // Costed on what it DOES, not what it is. 380 HP is a mid enemy; ten seconds of a
    // silent board is the wave, and the same reasoning that priced the flying healer at
    // 5 applies here — a disable has no raw stat to price it by.
    hexwright: 9,
    // Priced near a Brute rather than above it: the plate is answerable by position, and
    // a board that already spreads pays almost nothing for it.
    aegis: 5.5,
    // Costed well above their raw stats, and that gap IS the design note: "adjusted
    // upward for archetypes that demand a specific answer". Priced by stats alone, all
    // six together dropped daily generation to a 0.75 success rate — one day in four
    // failing solvability and falling back to an authored map. A flying healer is not a
    // 95 HP enemy to a board with no anti-air; it is the wave.
    vaulter: 5,
    hollow: 6,
    rimewrought: 10,
    'pall-warden': 13,
    mirrorkin: 8,
    cystbloom: 11,
    'boss-bulwark': 40,
    'boss-hive': 60,
    'boss-rust': 85,
    'boss-chorus': 110,
    'boss-prism': 160,
    'boss-comet': 50,
    'boss-bounder': 70,
    'boss-matriarch': 80,
    'boss-monolith': 95,
    'boss-aurora': 125,
  },

  /**
   * Earliest wave an archetype may appear (R-13-25).
   *
   * Staggered so each new threat arrives alone, on a non-spike wave, against a board the
   * player has had time to build. A generator that can open wave 2 with Menders is
   * technically valid and completely unfair.
   */
  archetypeGates: {
    runner: 1,
    sprinter: 3,
    brute: 4,
    ward: 7,
    splitter: 8,
    splitterling: 8,
    mender: 10,
    // Flyers earlier, and more of them. A board that only ever answers the path is a
    // board with one right shape; something that ignores it is what forces a rear guard.
    drifter: 7,
    shrike: 9,
    // Late enough that there is a board worth disabling. Arriving before one exists would
    // make it a slightly tanky walker and teach nothing.
    hexwright: 12,
    // Early enough to still be a placement lesson rather than a late-run tax.
    aegis: 6,
    // The back half of a run gets a new shape roughly every other wave, which is what
    // waves 13-28 never had.
    vaulter: 13,
    hollow: 15,
    rimewrought: 17,
    'pall-warden': 19,
    mirrorkin: 20,
    cystbloom: 21,
    'boss-bulwark': 5,
    'boss-hive': 10,
    'boss-rust': 15,
    'boss-chorus': 20,
    'boss-prism': 25,
    'boss-comet': 8,
    'boss-bounder': 12,
    'boss-matriarch': 14,
    'boss-monolith': 17,
    'boss-aurora': 21,
  },

  /**
   * The fight every run ends on. The middle of a run is a seeded draw; the finale is
   * not — Prism is the thing the whole game is teaching you to beat.
   */
  finalBoss: 'boss-prism',

  /**
   * Ten bosses, drawn seeded-uniform with no repeats within a run (B-20). `minWave`
   * still gates the earliest appearance; a boss drawn later than its gate scales +10%
   * HP per wave in `wavegen.ts`, so the pool stays fair without a strongest bias.
   */
  bossPool: [
    { enemy: 'boss-bulwark', minWave: 5 },
    { enemy: 'boss-comet', minWave: 8 },
    { enemy: 'boss-hive', minWave: 10 },
    { enemy: 'boss-bounder', minWave: 12 },
    { enemy: 'boss-matriarch', minWave: 14 },
    { enemy: 'boss-rust', minWave: 15 },
    { enemy: 'boss-monolith', minWave: 17 },
    { enemy: 'boss-chorus', minWave: 20 },
    { enemy: 'boss-aurora', minWave: 21 },
    { enemy: 'boss-prism', minWave: 24 },
  ],

  /**
   * Daily mutators (TRD-13 §6).
   *
   * Every one is expressible in the existing `SynergyEffect` union plus flat enemy stat
   * modifiers — no mutator-specific engine code (R-13-29). That was the point of the
   * exercise: nine mutators expressing cleanly is the evidence that TRD-08 §4 was
   * designed right.
   *
   * `threatBudgetMultiplier` compensates for the power swing. Without it, Glass Cannon
   * days would be trivial and Austerity days impossible.
   */
  mutators: [
    {
      id: 'frostbite',
      name: 'Frostbite',
      description: 'Enemies move 20% faster, but Chill lasts twice as long.',
      // Chill has no tick — it is a speed multiplier that expires. Authored against
      // `tickIntervalTicks` this reached nothing, so the day was the speed penalty with
      // none of the compensation.
      effects: [{ k: 'statusTuning', status: 'chill', durationMultiplier: 2 }],
      enemyModifiers: [{ stat: 'speed', op: 'mul', value: 1.2 }],
      threatBudgetMultiplier: 0.92,
      weight: 1,
    },
    {
      id: 'austerity',
      name: 'Austerity',
      description: 'Only 1 placement per round, but gold income is up 50%.',
      effects: [
        { k: 'runStat', stat: 'bonusPlacements', op: 'add', value: -1 },
        { k: 'runStat', stat: 'goldPerWave', op: 'add', value: 6 },
      ],
      enemyModifiers: [],
      threatBudgetMultiplier: 0.8,
      weight: 1,
    },
    {
      id: 'glass-cannon',
      name: 'Glass Cannon',
      description: 'All towers deal +50% damage. The Core has 8 HP.',
      effects: [
        { k: 'stat', target: { s: 'allTowers' }, stat: 'damageMultiplier', op: 'mul', value: 1.5 },
        { k: 'runStat', stat: 'bonusCoreHp', op: 'add', value: -12 },
      ],
      enemyModifiers: [],
      threatBudgetMultiplier: 1.15,
      weight: 1,
    },
    {
      id: 'gold-rush',
      name: 'Gold Rush',
      description: 'Start with 40 gold. Interest earns nothing.',
      // The gold half was MISSING: this shipped with only its downside, so the mutator
      // read as a pure penalty and nobody could tell why they were poor. Written as the
      // difference from the base rather than a bare 28, so the promise in the line above
      // stays true if STARTING_GOLD is ever retuned.
      effects: [
        { k: 'startingGold', amount: 40 - BALANCE.STARTING_GOLD },
        { k: 'runStat', stat: 'interestCap', op: 'set', value: 0 },
      ],
      enemyModifiers: [],
      threatBudgetMultiplier: 1.05,
      weight: 1,
    },
    {
      id: 'swarm-day',
      name: 'Swarm Day',
      description: 'Enemy counts up 60%, but each has 45% less health.',
      effects: [],
      enemyModifiers: [{ stat: 'hp', op: 'mul', value: 0.55 }],
      threatBudgetMultiplier: 1.6,
      weight: 1,
    },
    {
      id: 'fortified',
      name: 'Fortified',
      description: 'All enemies gain 4 armor. Every tower pierces 25% of armor.',
      effects: [
        { k: 'stat', target: { s: 'allTowers' }, stat: 'armorPierce', op: 'add', value: 0.25 },
      ],
      enemyModifiers: [{ stat: 'armor', op: 'add', value: 4 }],
      threatBudgetMultiplier: 0.95,
      weight: 1,
    },
    {
      id: 'blitz',
      name: 'Blitz',
      // Reworded to match what it DOES. There is no wave-cadence modifier in the system,
      // so "waves arrive far faster" promised something nothing could deliver; the
      // implementation is enemy speed, which is what Thin Ice and Quicksilver call it.
      description: 'Enemies move 35% faster, and waves pay 40% more.',
      effects: [{ k: 'runStat', stat: 'goldPerWave', op: 'add', value: 5 }],
      enemyModifiers: [{ stat: 'speed', op: 'mul', value: 1.35 }],
      threatBudgetMultiplier: 0.9,
      weight: 1,
    },
    {
      id: 'clean-sheet',
      name: 'Clean Sheet',
      description: 'The Core has 1 HP. You get 2 extra placements per round.',
      effects: [
        { k: 'runStat', stat: 'bonusCoreHp', op: 'add', value: -19 },
        { k: 'runStat', stat: 'bonusPlacements', op: 'add', value: 2 },
      ],
      enemyModifiers: [],
      threatBudgetMultiplier: 0.85,
      weight: 0.6,
    },
    {
      id: 'monoculture',
      name: 'Monoculture',
      // "only three classes" was the original wording, and it cannot be true: towers are
      // dual-class, so restricting the DRAW to three still puts their partner classes on
      // the cards. A strict reading leaves 1-4 towers of 37 and no playable board.
      description: 'The shop draws from three classes today, but synergies need one less.',
      effects: [
        { k: 'shopClassRestriction', classCount: 3 },
        { k: 'thresholdShift', delta: -1 },
      ],
      enemyModifiers: [],
      threatBudgetMultiplier: 0.9,
      weight: 0.8,
    },

    // ── The B-21 expansion — nine more, same rule: existing vocabulary only ──────────
    {
      id: 'overgrowth',
      name: 'Overgrowth',
      description: 'Enemies have 25% more health, but Poison stacks to 8.',
      effects: [{ k: 'statusTuning', status: 'poison', capOverride: 8 }],
      enemyModifiers: [{ stat: 'hp', op: 'mul', value: 1.25 }],
      threatBudgetMultiplier: 0.9,
      weight: 1,
    },
    {
      id: 'thin-ice',
      name: 'Thin Ice',
      description: 'Enemies move 15% faster, but the whole field flash-freezes every 12s.',
      effects: [
        {
          k: 'periodic',
          everyTicks: 720,
          then: [{ k: 'fieldStatus', status: 'freeze', durationTicks: 45, magnitude: 1 }],
        },
      ],
      enemyModifiers: [{ stat: 'speed', op: 'mul', value: 1.15 }],
      threatBudgetMultiplier: 1.0,
      weight: 1,
    },
    {
      id: 'high-stakes',
      name: 'High Stakes',
      description: 'Kills have a 12% chance to drop 2 gold, but waves pay 3 less.',
      effects: [
        { k: 'gold', amount: 2, trigger: 'kill', chance: 0.12 },
        { k: 'runStat', stat: 'goldPerWave', op: 'add', value: -3 },
      ],
      enemyModifiers: [],
      threatBudgetMultiplier: 1.0,
      weight: 0.8,
    },
    {
      id: 'crit-fever',
      name: 'Crit Fever',
      description: 'Every tower gains 15% crit chance, but crits hit for less.',
      effects: [
        { k: 'stat', target: { s: 'allTowers' }, stat: 'critChance', op: 'add', value: 0.15 },
        { k: 'stat', target: { s: 'allTowers' }, stat: 'critMultiplier', op: 'mul', value: 0.875 },
      ],
      enemyModifiers: [],
      threatBudgetMultiplier: 1.05,
      weight: 1,
    },
    {
      id: 'bulk-order',
      name: 'Bulk Order',
      description: 'Shop rerolls cost half, but waves pay 2 less.',
      effects: [
        { k: 'runStat', stat: 'rerollCostMultiplier', op: 'mul', value: 0.5 },
        { k: 'runStat', stat: 'goldPerWave', op: 'add', value: -2 },
      ],
      enemyModifiers: [],
      threatBudgetMultiplier: 1.0,
      weight: 1,
    },
    {
      id: 'duelists',
      name: 'Duelists',
      description: 'Synergies need one MORE tower, but every tower deals 20% more.',
      effects: [
        { k: 'thresholdShift', delta: 1 },
        { k: 'stat', target: { s: 'allTowers' }, stat: 'damageMultiplier', op: 'mul', value: 1.2 },
      ],
      enemyModifiers: [],
      threatBudgetMultiplier: 1.0,
      weight: 0.8,
    },
    {
      id: 'ironclad',
      name: 'Ironclad',
      description: 'The Core has 15 extra HP, but towers deal 15% less damage.',
      effects: [
        { k: 'runStat', stat: 'bonusCoreHp', op: 'add', value: 15 },
        { k: 'stat', target: { s: 'allTowers' }, stat: 'damageMultiplier', op: 'mul', value: 0.85 },
      ],
      enemyModifiers: [],
      threatBudgetMultiplier: 0.95,
      weight: 1,
    },
    {
      id: 'sniper-protocol',
      name: 'Sniper Protocol',
      description: 'Every tower reaches 1 tile further, but fires 20% slower.',
      effects: [
        { k: 'stat', target: { s: 'allTowers' }, stat: 'rangeBonus', op: 'add', value: 1 },
        {
          k: 'stat',
          target: { s: 'allTowers' },
          stat: 'attackSpeedMultiplier',
          op: 'mul',
          value: 0.8,
        },
      ],
      enemyModifiers: [],
      threatBudgetMultiplier: 0.95,
      weight: 1,
    },
    {
      id: 'quicksilver',
      name: 'Quicksilver',
      description: 'Enemies have 20% less health but move 30% faster.',
      effects: [],
      enemyModifiers: [
        { stat: 'hp', op: 'mul', value: 0.8 },
        { stat: 'speed', op: 'mul', value: 1.3 },
      ],
      threatBudgetMultiplier: 1.15,
      weight: 0.8,
    },
  ],

  tuning: {
    /** THE balance lever: path length is proportional to total tower uptime (R-13-08). */
    pathLength: [45, 70],
    waveCount: [22, 28],
    baseThreat: 6,
    /**
     * Counts. This buys enemies at their threat cost, so it is the NUMBERS half of a
     * wave; `ENEMY_HP_GROWTH` in the sim is the health half. Kept deliberately smaller than
     * the health move, and they multiply: the complaint was never that late waves were
     * short, and counts are the axis splash damage is already best at answering.
     */
    threatGrowth: 1.175,
    /**
     * The late-run ramp (playtest request). Runs were flattening out: past the midpoint a
     * board that was working kept working, because threat grew at the same rate the board
     * did. An extra 6% per wave from 15 on compounds to roughly 1.8x by wave 25 — enough
     * that the last third asks a new question, and worth nothing at all before then.
     *
     * 5% -> 7% on the second round of "a little too easy", which compounds to about 2.1x
     * by wave 25 rather than 1.7x. The six late archetypes added in the same pass push
     * the same direction.
     *
     * Raising this looked impossible at first and the false trail is worth recording.
     * At 1.055 and 1.06 daily generation collapsed to a 0.75 success rate — one day in
     * four falling back to an authored map — which reads exactly like a difficulty
     * ceiling enforced by the solvability probe. It was not. `maxAttempts` was 50, and
     * the seeds were fine; the generator was simply giving up before it found them. With
     * the attempt budget raised, 1.07 and even 1.08 generate 28 days out of 28.
     *
     * So the gate was never the binding constraint. Read a falling generation rate as
     * "look harder", not "this is too hard" — and measure the rate directly, because a
     * probe's death WAVE is far too coarse to show a 6% change in threat.
     */
    lateThreatGrowth: 1.07,
    lateGrowthFromWave: 15,
    spikeMultiplier: 1.3,
    reliefMultiplier: 0.85,
    terrainDensity: [0.12, 0.2],
    /**
     * Was 50, which silently capped how much variety and difficulty the game could carry:
     * every archetype added to the pool gives the generator more ways to roll a wave the
     * reference player cannot answer, so it needs more tries to land one it can. Six new
     * late archetypes took the generation rate from ~0.9 to 0.75 at 50 attempts and back
     * to 1.00 at 140, on the same seeds — nothing was unsolvable, the search was just too
     * short. Costs nothing that matters: worst-case generation measured 78ms, and the
     * result is cached per (date, simVersion) in Postgres anyway.
     */
    maxAttempts: 140,
    /**
     * The reference player must reach this wave or the seed is rerolled (R-13-34).
     *
     * Derived from measurement, not chosen. On the three AUTHORED maps — hand-validated
     * and cleared to wave 25 by a human — the reference policy averages waves 9.2 / 7.4 /
     * 10.4 and bottoms out at 7. Six leaves margin below that floor.
     *
     * The bar exists to reject seeds that are IMPOSSIBLE, not seeds that are hard: a map
     * with no build space beside its path fails catastrophically, in the first few waves,
     * and that is what this catches. Setting it near a good player's ceiling would reject
     * most of the interesting maps instead.
     *
     * `daily.test.ts` asserts the authored maps still clear it — if balance shifts far
     * enough to break that, this number needs re-deriving rather than nudging.
     */
    probeTargetWave: 6,
  },
};
